import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  Container,
  Flex,
  Icon,
  IconButton,
  Heading,
  Text,
  Stack,
  Spacer,
  StackSeparator,
} from '@chakra-ui/react';
import { NavLink, useParams } from 'react-router-dom';
import { PlusIcon, PencilIcon, TrashIcon, ShareIcon, Bars3Icon } from '@heroicons/react/24/outline';
import DestructiveModal from '@/components/alert-dialogs/destructive-modal';
import { MenuContent, MenuItem, MenuRoot, MenuTrigger } from '@/components/ui/menu';
import { Tooltip } from '@/components/ui/tooltip';
import { toaster } from '@/components/ui/toaster';
import QuickSearchForm from '@/components/ui/quick-search-form';
import WorkspaceLayout from '@/pages/workspace/workspace-layout';
import CreateContentTypeModal from '@/pages/workspace/create-content-type-modal';
import ShareLink from './share-link';
import useCollectionTypeStore from '@/utils/use-collection-type';
import useModalToggle from '@/lib/hooks/use-modal-toggle';
import useIsInAdminGroup from '@/lib/hooks/use-is-in-admin-group';
import useIsCancelled from '@/lib/hooks/use-is-cancelled';
import useWorkspace from '@/lib/hooks/use-workspace';
import { CollectionType } from '@/lib/types';
import api from '@/utils/api';

interface Props {
  item: CollectionType;
  onUpdateList: () => Promise<void>;
}

const CollectionItem = ({ item, onUpdateList }: Props) => {
  const { workspaceId } = useParams();
  const [state, setState] = useState({
    isLoading: false,
    showDeleteModal: false,
    showShareLinkModal: false,
  });
  const [selectedType, setSelectedType] = useState<CollectionType | undefined>(undefined);

  const handleDelete = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, isLoading: true }));

      const promise = api.delete(`collection-types/${item.id}`);

      toaster.promise(promise, {
        success: {
          title: 'Content collection was removed successfully',
        },
        error: {
          title: 'Failed to remove content collection',
        },
        loading: { title: 'Removing...', description: 'Please wait' },
      });

      await promise;
      setState((prev) => ({ ...prev, showDeleteModal: false }));
      await onUpdateList();
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.message || 'Failed to delete content collection',
      });
    } finally {
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  }, [item.id, onUpdateList]);

  const handleGenerateLink = useCallback(() => {
    setState((prev) => ({ ...prev, showShareLinkModal: true }));
    setSelectedType(item);
  }, [item]);

  const menuActions = (
    <>
      <NavLink key={item.id} to={`/${workspaceId}/collection-types/${item.id}/edit`}>
        <Text color={'gray.700'} fontSize={'md'} fontWeight={'medium'}>
          {item.name}
        </Text>
      </NavLink>
      <Spacer />
    </>
  );

  return (
    <>
      <Stack>
        <Flex direction='row' alignItems='center'>
          {menuActions}

          <Stack direction='row' display={{ base: 'none', md: 'block' }}>
            <Tooltip content={'Edit'}>
              <IconButton variant={'ghost'} aria-label={'Edit'} size={'sm'}>
                <NavLink
                  key={`edit-${item.id}`}
                  to={`/${workspaceId}/collection-types/${item.id}/edit`}
                >
                  <Icon as={PencilIcon} />
                </NavLink>
              </IconButton>
            </Tooltip>
            <Tooltip content={'Generate link'}>
              <IconButton
                variant={'ghost'}
                aria-label={'Generate link'}
                size={'sm'}
                onClick={handleGenerateLink}
              >
                <Icon as={ShareIcon} />
              </IconButton>
            </Tooltip>
            <Tooltip content={'Delete'}>
              <IconButton
                variant={'ghost'}
                aria-label={'Delete'}
                size={'sm'}
                onClick={() => setState((prev) => ({ ...prev, showDeleteModal: true }))}
              >
                <Icon as={TrashIcon} />
              </IconButton>
            </Tooltip>
          </Stack>

          <Box display={{ base: 'block', md: 'none' }}>
            <MenuRoot>
              <MenuTrigger asChild>
                <IconButton variant='outline' aria-label='Menu' size='sm'>
                  <Icon as={Bars3Icon} />
                </IconButton>
              </MenuTrigger>
              <MenuContent>
                <MenuItem value='edit' asChild>
                  <NavLink
                    key={`mobile-edit-${item.id}`}
                    to={`/${workspaceId}/collection-types/${item.id}/edit`}
                  >
                    <Icon as={PencilIcon} w={4} h={4} />
                    Edit
                  </NavLink>
                </MenuItem>
                <MenuItem value='generate-link' onClick={handleGenerateLink}>
                  <Icon as={ShareIcon} w={4} h={4} />
                  Generate link
                </MenuItem>
                <MenuItem
                  value='delete'
                  onClick={() => setState((prev) => ({ ...prev, showDeleteModal: true }))}
                >
                  <Icon as={TrashIcon} w={4} h={4} />
                  Delete
                </MenuItem>
              </MenuContent>
            </MenuRoot>
          </Box>
        </Flex>
      </Stack>

      <DestructiveModal
        isOpen={state.showDeleteModal}
        onCancel={() => setState((prev) => ({ ...prev, showDeleteModal: false }))}
        onConfirm={handleDelete}
        title={`Remove ${item.name} from content libraries?`}
        confirmText={'Delete'}
        isLoading={state.isLoading}
      >
        <Text fontSize='sm' color='gray.600'>
          This action will permanently delete all related content and cannot be undone.
        </Text>
      </DestructiveModal>

      {state.showShareLinkModal && selectedType && (
        <ShareLink
          isOpen={state.showShareLinkModal}
          onClose={() => setState((prev) => ({ ...prev, showShareLinkModal: false }))}
          item={selectedType}
          reloadContents={onUpdateList}
        />
      )}
    </>
  );
};

export default function ListCollectionType() {
  const { collectionTypes, loadCollectionTypes } = useCollectionTypeStore();
  const isAdmin = useIsInAdminGroup();
  const isCancelled = useIsCancelled();
  const [searchResults, setSearchResults] = useState<CollectionType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { workspaceId } = useParams();
  const { workspace } = useWorkspace();
  const createModal = useModalToggle();

  useEffect(() => {
    const fetchData = async () => {
      if (workspaceId) {
        setIsLoading(true);
        try {
          await loadCollectionTypes(workspaceId);
        } catch (err: any) {
          toaster.create({
            type: 'error',
            title: err.response?.data?.message || 'Failed to load collection types',
          });
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchData();
  }, [workspaceId, loadCollectionTypes]);

  useEffect(() => {
    setSearchResults(collectionTypes as CollectionType[]);
  }, [collectionTypes]);

  const refreshCollectionTypes = useCallback(async () => {
    if (workspaceId) {
      await loadCollectionTypes(workspaceId);
    }
  }, [workspaceId, loadCollectionTypes]);

  const handleSearch = useCallback(
    (search: string) => {
      if (search.length === 0) {
        setSearchResults(collectionTypes as CollectionType[]);
        return;
      }

      const filtered = collectionTypes.filter((item) =>
        item.name.toLowerCase().includes(search.toLowerCase()),
      );

      setSearchResults(filtered as CollectionType[]);
    },
    [collectionTypes],
  );

  return (
    <WorkspaceLayout>
      <Card.Root minH={'100vh'} mx={'auto'} width={{ md: '90%' }} maxW={'6xl'}>
        <Card.Header>
          <Container maxW={'4xl'}>
            <Flex
              direction={{ base: 'column', md: 'row' }}
              alignItems={{ base: 'normal', md: 'center' }}
              mb={4}
            >
              <Heading size='3xl' mb={{ base: 4, md: 0 }}>
                Content collections
              </Heading>
              <Spacer />
              {isAdmin && !isCancelled && (
                <Stack gap={4} direction={{ base: 'column', md: 'row' }}>
                  <Button colorPalette='brand' asChild>
                    <NavLink to={`/${workspaceId}/collection-types/create`}>
                      <Icon as={PlusIcon} w={4} h={4} />
                      Create custom collection
                    </NavLink>
                  </Button>
                </Stack>
              )}
            </Flex>
          </Container>
        </Card.Header>
        <Card.Body>
          <Container maxW={'4xl'}>
            <Box mb={8}>
              <QuickSearchForm handleSearch={handleSearch} />
            </Box>

            {isLoading ? (
              <Flex justify='center' py={8}>
                <Text>Loading collections...</Text>
              </Flex>
            ) : searchResults.length === 0 ? (
              <Flex justify='center' direction='column' align='center' py={8}>
                <Text fontSize='lg' mb={4}>
                  No collections found
                </Text>
                {isAdmin && !isCancelled && (
                  <Button colorPalette='brand' asChild>
                    <NavLink to={`/${workspaceId}/collection-types/create`}>
                      <Icon as={PlusIcon} w={4} h={4} />
                      Create your first collection
                    </NavLink>
                  </Button>
                )}
              </Flex>
            ) : (
              <Stack gap={4} separator={<StackSeparator />}>
                {searchResults.map((item) => (
                  <CollectionItem key={item.id} item={item} onUpdateList={refreshCollectionTypes} />
                ))}
              </Stack>
            )}
          </Container>
        </Card.Body>
      </Card.Root>

      {workspace && workspaceId && workspace.sport_type_id && (
        <CreateContentTypeModal
          workspaceId={parseInt(workspaceId, 10)}
          sportTypeId={workspace.sport_type_id}
          {...createModal.props}
          onSuccess={createModal.close}
        />
      )}
    </WorkspaceLayout>
  );
}
